import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';
import { ApiError, get, post } from './client';
import type { AgentRunDetail, AgentRunSummary, AgentSession, AgentSessionDetail, ApiToken, AutonomyProfile, Catalog, Group, MapRecord, MapSummary, Me, MediaItem, PerRobotResult, ScanEdits, Schedule, ScheduleHistoryItem, Site, Tour, VoicesResponse } from './types';
import { uiSocket } from '../live/socket';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

// §14: session created/updated/closed → patch lists; a run finished → refetch that session's log.
uiSocket.on((m) => {
  if (m.type === 'agent-session') {
    for (const key of [qk.sessions('active'), qk.sessions('all')])
      queryClient.setQueryData<AgentSession[]>(key, (old) =>
        old?.map((s) => (s.id === m.id ? { ...s, state: m.state, title: m.title ?? s.title, robots: m.robots ?? s.robots } : s)),
      );
    void queryClient.invalidateQueries({ queryKey: ['agent-sessions'] });
    void queryClient.invalidateQueries({ queryKey: qk.session(m.id) });
  } else if (m.type === 'agent' && m.sessionId && (m.kind === 'done' || m.kind === 'error')) {
    void queryClient.invalidateQueries({ queryKey: ['agent-sessions'] });
    void queryClient.invalidateQueries({ queryKey: qk.session(m.sessionId) });
  }
});

// §18: schedule runs → refresh list (next run / last run) and that schedule's history.
uiSocket.on((m) => {
  if (m.type !== 'schedule') return;
  void queryClient.invalidateQueries({ queryKey: qk.schedules });
  void queryClient.invalidateQueries({ queryKey: qk.scheduleHistory(m.id) });
});

// §6.1: another client (or a robot upload) changed a map → refresh it.
uiSocket.on((m) => {
  if (m.type !== 'map-updated') return;
  void queryClient.invalidateQueries({ queryKey: ['map', m.id] });
  void queryClient.invalidateQueries({ queryKey: ['maps'] });
});

export const qk = {
  me: ['me'] as const,
  catalog: ['catalog'] as const,
  voices: ['voices'] as const,
  groups: ['groups'] as const,
  sites: ['sites'] as const,
  updates: ['updates'] as const,
  maps: ['maps'] as const,
  map: (id: string) => ['map', id] as const,
  /** §6.2 saved scan edits (under the map key, so map invalidation refreshes them too) */
  scanEdits: (id: string) => ['map', id, 'scan-edits'] as const,
  media: ['media'] as const,
  mediaItem: (id: string) => ['media', id] as const,
  mediaStatus: ['media-status'] as const,
  tours: (mapId: string) => ['tours', mapId] as const,
  robotTours: (serial: string) => ['robot-tours', serial] as const,
  agentRuns: ['agent-runs'] as const,
  agentRun: (id: string) => ['agent-run', id] as const,
  tokens: ['api-tokens'] as const,
  sessions: (state: 'active' | 'all') => ['agent-sessions', state] as const,
  schedules: ['schedules'] as const,
  scheduleHistory: (id: string) => ['schedule-history', id] as const,
  profiles: ['autonomy-profiles'] as const,
  tools: ['tools'] as const,
  session: (id: string) => ['agent-session', id] as const,
};

const EMPTY_CATALOG: Catalog = { motions: [], emotions: [], dances: [], lightEffects: {}, actionTypes: [] };

export const useMe = () => useQuery({ queryKey: qk.me, queryFn: () => get<Me>('/api/me'), staleTime: 60_000 });
export const useCatalog = () =>
  useQuery({ queryKey: qk.catalog, queryFn: () => get<Catalog>('/api/catalog'), staleTime: Infinity, placeholderData: EMPTY_CATALOG });
export const useVoices = () => useQuery({ queryKey: qk.voices, queryFn: () => get<VoicesResponse>('/api/speech/voices'), staleTime: 5 * 60_000 });
export const useGroups = () => useQuery({ queryKey: qk.groups, queryFn: () => get<Group[]>('/api/groups') });
/** §28 sites; robots carry their own `site`, so this list is only for the tabs / pickers / the manage dialog. */
export const useSites = () => useQuery({ queryKey: qk.sites, queryFn: () => get<Site[]>('/api/sites') });
export const useMaps = () => useQuery({ queryKey: qk.maps, queryFn: () => get<MapSummary[]>('/api/maps') });
export const useMapRecord = (id: string | undefined) =>
  useQuery({ queryKey: qk.map(id ?? ''), queryFn: () => get<MapRecord>(`/api/maps/${id}`), enabled: Boolean(id), staleTime: 60_000 });
/** §6.2 saved pencil / eraser strokes of a map (only fetched while the scan editor is open). */
export const useScanEdits = (id: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: qk.scanEdits(id ?? ''), queryFn: () => get<ScanEdits>(`/api/maps/${id}/scan-edits`), enabled: Boolean(id) && enabled, staleTime: 30_000 });
export const useMediaList = () => useQuery({ queryKey: qk.media, queryFn: () => get<MediaItem[]>('/api/media') });
export const useMediaItem = (id: string | undefined) =>
  useQuery({ queryKey: qk.mediaItem(id ?? ''), queryFn: () => get<MediaItem>(`/api/media/${id}`), enabled: Boolean(id) });
export const useTours = (mapId: string | undefined) =>
  useQuery({ queryKey: qk.tours(mapId ?? ''), queryFn: () => get<Tour[]>(`/api/maps/${mapId}/tours`), enabled: Boolean(mapId) });
export const useRobotTours = (serial: string | undefined) =>
  useQuery({ queryKey: qk.robotTours(serial ?? ''), queryFn: () => get<Tour[]>(`/api/robots/${encodeURIComponent(serial ?? '')}/tours`), enabled: Boolean(serial) });
export const useAgentRuns = () => useQuery({ queryKey: qk.agentRuns, queryFn: () => get<AgentRunSummary[]>('/api/agent/runs'), refetchInterval: 15_000 });
export const useAgentRun = (id: string | undefined) =>
  useQuery({ queryKey: qk.agentRun(id ?? ''), queryFn: () => get<AgentRunDetail>(`/api/agent/runs/${id}`), enabled: Boolean(id) });
export const useAgentSessions = (state: 'active' | 'all') =>
  useQuery({ queryKey: qk.sessions(state), queryFn: () => get<AgentSession[]>(`/api/agent/sessions?state=${state}`), refetchInterval: 30_000 });
export const useAgentSession = (id: string | undefined) =>
  useQuery({ queryKey: qk.session(id ?? ''), queryFn: () => get<AgentSessionDetail>(`/api/agent/sessions/${id}`), enabled: Boolean(id) });

/** Tours of every map (flattened), plus whether the tours API is missing. */
export function useAllTours() {
  const maps = useMaps();
  const qs = useQueries({ queries: (maps.data ?? []).map((m) => ({ queryKey: qk.tours(m.id), queryFn: () => get<Tour[]>(`/api/maps/${m.id}/tours`) })) });
  return { tours: qs.flatMap((q) => q.data ?? []), missing: qs.some((q) => isMissing(q.error)), maps: maps.data ?? [] };
}

export const useSchedules = () => useQuery({ queryKey: qk.schedules, queryFn: () => get<Schedule[]>('/api/schedules'), refetchInterval: 60_000 });
export const useScheduleHistory = (id: string | undefined) =>
  useQuery({ queryKey: qk.scheduleHistory(id ?? ''), queryFn: () => get<ScheduleHistoryItem[] | { items: ScheduleHistoryItem[] }>(`/api/schedules/${id}/history`), enabled: Boolean(id) });
export const useAutonomyProfiles = () => useQuery({ queryKey: qk.profiles, queryFn: () => get<AutonomyProfile[]>('/api/autonomy/profiles') });
export const useTools = () => useQuery({ queryKey: qk.tools, queryFn: () => get<{ name: string; description?: string }[]>('/api/tools'), staleTime: Infinity });

export const useApiTokens = () => useQuery({ queryKey: qk.tokens, queryFn: () => get<ApiToken[]>('/api/auth/tokens') });

/** True if the error is "endpoint not implemented on this server". */
export const isMissing = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405 || e.status === 501);

// ---------------------------------------------------------------- fleet helpers

export interface Targets {
  robots?: string[];
  group?: string;
}

export async function fleetCmd(targets: Targets, name: string, args: Record<string, unknown> = {}) {
  return post<{ results: PerRobotResult[] }>('/api/fleet/cmd', { ...targets, name, args });
}

/** Stop: zero drive frame on the socket right away (deadman), then the `stop` command. */
export async function stopRobots(serials: string[]) {
  for (const s of serials) uiSocket.send({ type: 'drive', robotId: s, v: 0, w: 0 });
  if (!serials.length) return { results: [] as PerRobotResult[] };
  return fleetCmd({ robots: serials }, 'stop');
}
